import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteProps,
  Typography,
} from "@mui/material";
import { FastField, Field, FieldInputProps, FieldMetaProps } from "formik";
import React, { useRef } from "react";

import { palette } from "~frontend/theme";

import { Checkbox } from "../Checkbox";
import FormTextField from "./FormTextField";

export type AutocompleteValue = {
  id: string;
  label: string;
  selectedLabel?: string;
  type?: "groupHeading" | "groupMember"; // default: top-level item
  groupId?: string;
};

type FormInMemoryComboboxProps = {
  label: string;
  error: boolean;
  helperText: React.ReactNode;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  transformNewValue?: (
    newValue: AutocompleteValue[],
    oldValue: AutocompleteValue[]
  ) => AutocompleteValue[];
} & Omit<
  AutocompleteProps<AutocompleteValue, true, false, false>,
  "renderInput"
>;

export const optionIcon = <CheckBoxOutlineBlank fontSize="small" />;
export const selectedOptionIcon = (
  <CheckBox fontSize="small" sx={{ color: palette.darkGreen }} />
);

function OptionContent({
  option,
  selected,
}: {
  option: AutocompleteValue;
  selected: boolean;
}): React.ReactElement {
  if (option.type === "groupHeading") {
    return (
      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
        {option.label}
      </Typography>
    );
  }

  return (
    <>
      <Checkbox
        icon={optionIcon}
        checkedIcon={selectedOptionIcon}
        style={{
          marginRight: 8,
          ...(option.type === "groupMember"
            ? {
                marginLeft: 16,
              }
            : {}),
        }}
        checked={selected}
      />

      <Typography variant="body2">{option.label}</Typography>
    </>
  );
}

export function FormInMemoryCombobox(
  props: FormInMemoryComboboxProps
): React.ReactElement {
  const currentValue = useRef<AutocompleteValue[]>(props.value || []);
  function handleChange(
    event: React.SyntheticEvent,
    newValue: AutocompleteValue[],
    reason: AutocompleteChangeReason
  ) {
    if (props.transformNewValue) {
      const transformed = props.transformNewValue(
        newValue,
        currentValue.current
      );
      currentValue.current = transformed;
      props.onChange?.(event, transformed, reason);
    } else {
      currentValue.current = newValue;
      props.onChange?.(event, newValue, reason);
    }
  }

  return (
    <Autocomplete
      disableCloseOnSelect
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Typography
            variant="body1"
            component="span"
            {...getTagProps({ index })}
            key={`auto-complete-value-${index}-${option.id}`}
          >
            {option.selectedLabel ?? option.label}
            {index < value.length - 1 ? "," : ""}
          </Typography>
        ))
      }
      multiple
      getOptionLabel={option => option.label}
      getOptionKey={option => option.id}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      ListboxProps={{ style: { maxHeight: "250px" } }}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: "preventOverflow",
              enabled: true,
            },
          ],
        },
      }}
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          style={
            selected
              ? {
                  backgroundColor: palette.lightGray,
                  boxShadow: "0px 0px 0px 2px white inset",
                }
              : undefined
          }
        >
          <OptionContent option={option} selected={selected} />
        </li>
      )}
      renderInput={params => (
        <FormTextField
          {...params}
          error={props.error}
          helperText={props.helperText}
          InputProps={{
            ...(params.InputProps || {}),
            startAdornment: (
              <>
                {props.startAdornment}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {props.endAdornment}
              </>
            ),
          }}
          variant="outlined"
          label={props.label}
        />
      )}
      getOptionDisabled={option => option.type === "groupHeading"}
      {...props}
      onChange={handleChange}
    />
  );
}

export function FormikInMemoryCombobox({
  name,
  shouldUpdate,
  fast,
  overrideFormikProps,
  bottomLabel,
  ...childProps
}: Omit<
  FormInMemoryComboboxProps,
  "value" | "onChange" | "renderInput" | "error" | "helperText"
> & {
  name: string;
  shouldUpdate?: (props: any, prevProps: any) => boolean;
  fast?: boolean;
  overrideFormikProps?: (
    field: FieldInputProps<string[]>,
    meta: FieldMetaProps<string[]>
  ) => FieldInputProps<string[]>;
  bottomLabel?: string;
}): React.ReactElement {
  const Component = fast ? FastField : Field;
  return (
    <Component name={name} shouldUpdate={shouldUpdate}>
      {({
        field,
        meta,
        form,
      }: {
        field: FieldInputProps<string[]>;
        meta: FieldMetaProps<string[]>;
        form: any;
      }) => (
        <FormInMemoryCombobox
          {...(overrideFormikProps ? overrideFormikProps(field, meta) : field)}
          {...childProps}
          multiple
          value={childProps.options.filter(({ id }) =>
            field.value.includes(id)
          )}
          onChange={(event, options) => {
            form.setFieldValue(
              name,
              options.map(o => o.id),
              true
            );
          }}
          error={meta.touched && Boolean(meta.error)}
          helperText={
            meta.touched && Boolean(meta.error) ? meta.error : bottomLabel
          }
        />
      )}
    </Component>
  );
}
